import { Component, OnInit, Inject } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

//SERVICES

//CONFIG
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// PIPE
import { DecimalPlacesPipe } from '../../../shared/pipes/decimal-places.pipe';
import { KitApiService } from '../../../shared/services/api/kit-api.service';

//UTILS
import { camelCaseToSnakeCaseObjectKeys } from '../../../shared/util/string-transformation.utils';
import { IUnitOfMeasure } from '../../../unit-of-measure';
import { IKitItem } from '../../interfaces';

//INTERFACES
interface IDialogData {
  kit :any;
}

@Component({
  selector: 'lum-rfq-kit-items-add-form',
  templateUrl: './rfq-kit-items-add-form.component.html',
  styleUrls: ['./rfq-kit-items-add-form.component.scss'],
})
export class RfqKitItemsAddFormComponent implements OnInit {
  form :UntypedFormGroup;
  submitting :boolean = false;
  loading :boolean = false;

  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private kitApiService :KitApiService,
    private snackBar :MatSnackBar,
    public dialogRef :MatDialogRef<RfqKitItemsAddFormComponent>,
    private decimalPlacesPipe :DecimalPlacesPipe,
    @Inject(MAT_DIALOG_DATA) public data :IDialogData,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.initItems();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onSubmit() :void {
    const formData = this.form.value;
    const kitComponentsForPO :any[] = [];

    if (formData.items.length > 0) {
      formData.items.forEach((item :IKitItem) => {
        const product = item.product;
        const basePrice = product.basePrice
          ? camelCaseToSnakeCaseObjectKeys(product.basePrice)
          : null;
        const quotations =
          product.quotations && product.quotations.length > 0
            ? product.quotations
            : [];
        const unitOfMeasures =
          product.unitOfMeasures && product.unitOfMeasures.length > 0
            ? product.unitOfMeasures.map((item :IUnitOfMeasure) =>
                camelCaseToSnakeCaseObjectKeys(item),
              )
            : [];

        const kitComponent = {
          id: product.id,
          base_price: basePrice,
          custom_field_values: [],
          default_purchase_unit_id: product.defaultPurchaseUnitId,
          default_selling_unit_id: product.defaultSellingUnitId,
          discount_structure: product.discountStructure,
          is_kit: product.isKit,
          markup_structure: product.markupStructure,
          oss_limit_notify: product.ossLimitNotify,
          parent_id: product.variantParentId,
          photo: product.photo,
          product_category_id: product.productCategoryId,
          product_description: product.productDescription,
          product_name: product.productName,
          qty_available: product.qtyAvailable,
          quotations: quotations,
          retail_price: product.retailPrice,
          sellable: product.sellable,
          sku: product.internalSku,
          sub_category_id: product.subCategoryId,
          tags: [],
          total_variant_skus: product.totalVariantSkus,
          type: product.type,
          unit_class_id: product.unitClassId,
          unit_of_measures: unitOfMeasures,
          unit_price: product.unitPrice,
          variant_parent: product.variantParent,
          quantity: +item.quantity,
          uom_id: item.uomId,
        };

        kitComponentsForPO.push(kitComponent);
      });
    }

    this.dialogRef.close(kitComponentsForPO);
  }

  onQuantityChange(event :any) :void {
    const quantity = event.target.value ?? 0;
    if (quantity < 0) return;

    this.itemsFormArray.controls.forEach((item :any) => {
      item.patchValue({
        quantity: this.decimalPlacesPipe.transform(
          quantity * item.value.originalQuantity,
        ),
      });
    });
  }

  private initForm() :void {
    this.form = this.fb.group({
      quantity: [1, [Validators.required, Validators.min(0)]],
      items: this.fb.array([], [this.validateMinArrayLength.bind(this)]),
    });
  }

  private initItems() :void {
    this.loading = true;
    const relations = [
      'product.unitOfMeasures',
      'product.basePrice',
      'product.quotations',
      'product.variantParent',
    ].join(',');

    this.kitApiService
      .getByKitId(this.data.kit.id, {
        relations: relations,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          if (response.length > 0) {
            response.forEach((item :any) => {
              const product = item.product || null;
              const itemFormGroup = this.fb.group({
                product: [product],
                sku: [
                  product ? `${product.productName} [${product.internalSku}]` : '',
                ],
                originalQuantity: [item.baseUomQuantity || 0],
                quantity: [
                  item.baseUomQuantity || 0,
                  [Validators.required, Validators.min(0)],
                ],
                uomId: [item.uomId || 0, Validators.required],
              });
              this.itemsFormArray.push(itemFormGroup);
            });
          }
          this.loading = false;
        },
        error: (response :any) => {
          this.snackBar.open(response.error, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.loading = false;
        },
      });
  }

  private validateMinArrayLength(
    control :AbstractControl,
  ) :ValidationErrors | null {
    const items = control.value;

    if (items.length <= 0) {
      return { items: 'Kit must contain 1 or more rfq components' };
    }

    return null;
  }

  get itemsFormArray() :UntypedFormArray {
    return this.form.get('items') as UntypedFormArray;
  }
}
